import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import MessageComposer from '../MessageComposer';
import '../ThreadQuill.css';
import MessageBubble from '../MessageBubble';
import {
  fetchOversightThread,
  fetchOversightMoreMessages,
  sendOversightMessage,
  markOversightConversationUnread,
  fetchOversightUnreadCounts,
  clearOversightThread,
} from '../store/adminOversightSlice';


const AdminOversightThreadPage = ({ match }) => {
  const { id: conversationId } = match.params;
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { activeThread, messages, nextCursor, loading } = useSelector(
    ({ adminOversight }) => adminOversight
  );
  const bottomRef = useRef(null);
  const initialScrollDone = useRef(false);

  const isAssistantPath = location.pathname.includes('/oversight/assistant/');
  const backPath = isAssistantPath ? '/admin/oversight/assistant' : '/admin/oversight/coach';
  const backLabel = isAssistantPath
    ? 'Back to Assistant Conversations'
    : 'Back to Coach Conversations';

  useEffect(() => {
    dispatch(fetchOversightThread(conversationId));
    dispatch(fetchOversightUnreadCounts());
    return () => dispatch(clearOversightThread());
  }, [dispatch, conversationId]);

  const scrollToBottom = useCallback((behavior) => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: behavior || 'auto', block: 'end' });
    }
  }, []);

  useEffect(() => {
    if (!initialScrollDone.current && messages.length > 0) {
      scrollToBottom('auto');
      initialScrollDone.current = true;
    }
  }, [messages, scrollToBottom]);

  const handleLoadMore = () => {
    if (nextCursor) dispatch(fetchOversightMoreMessages(conversationId, nextCursor));
  };

  const coach = activeThread?.coachUserId;
  const player = activeThread?.playerProfileId;
  const coachName = coach ? `${coach.firstName} ${coach.lastName}` : 'Coach';
  const playerName = player ? `${player.firstName} ${player.lastName}` : 'Player';

  const getSenderLabel = msg => {
    if (msg.senderType === 'admin') return 'Kajetan Borecki';
    if (msg.senderType === 'coach') return `${isAssistantPath ? 'Asystent' : 'Trener'}: ${coachName}`;
    if (msg.senderType === 'player') return `Sportowiec: ${playerName}`;
    return undefined;
  };

  if (loading && !activeThread) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
        Loading conversation...
      </div>
    );
  }

  return (
    <div style={{ padding: '32px 0' }}>
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => history.push(backPath)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#2e496b',
              fontSize: '14px',
              padding: 0,
            }}
          >
            &#8592; {backLabel}
          </button>
          <button
            type="button"
            onClick={async () => {
              await dispatch(markOversightConversationUnread(conversationId));
              history.push(backPath);
            }}
            style={{
              background: 'none',
              border: '1px solid #cbd5e1',
              borderRadius: '4px',
              cursor: 'pointer',
              color: '#64748b',
              fontSize: '12px',
              padding: '4px 10px',
              marginLeft: '16px',
            }}
          >
            Oznacz jako nieprzeczytane
          </button>
        </div>
        <h2 style={{ margin: '8px 0 0', fontSize: '18px', color: '#1e293b' }}>
          {isAssistantPath ? 'Asystent' : 'Trener'}: {coachName}
          {coach?.schoolName && (
            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '400', marginLeft: '8px' }}>
              {coach.schoolName}
            </span>
          )}
          <span style={{ fontSize: '14px', color: '#94a3b8', fontWeight: '400', margin: '0 8px' }}>↔</span>
          Sportowiec: {playerName}
          {player?.sport && (
            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '400', marginLeft: '8px' }}>
              {player.sport}
            </span>
          )}
        </h2>
      </div>

      <div
        style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {/* Message list */}
        <div style={{ padding: '0' }}>
          {nextCursor && (
            <div style={{ textAlign: 'center', padding: '12px' }}>
              <button
                type="button"
                onClick={handleLoadMore}
                style={{
                  background: 'none',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  padding: '4px 12px',
                  fontSize: '12px',
                  color: '#64748b',
                  cursor: 'pointer',
                }}
              >
                Load earlier messages
              </button>
            </div>
          )}
          {messages.map(msg => (
            <MessageBubble
              key={msg._id}
              message={msg}
              isSentByMe={msg.senderType === 'admin'}
              senderLabel={getSenderLabel(msg)}
              senderType={msg.senderType}
            />
          ))}
          <div ref={bottomRef} />
        </div>

        {/* Reply input */}
        <MessageComposer
          threadType="conversations"
          conversationId={conversationId}
          placeholder="Type a message as Admin..."
          onSend={({ text, file, draftRevision }) => dispatch(sendOversightMessage({ conversationId, text, file, draftRevision }))}
          onSent={() => { dispatch(fetchOversightUnreadCounts()); setTimeout(() => scrollToBottom('smooth'), 50); }}
        />
      </div>
    </div>
  );
};

export default AdminOversightThreadPage;
